import { useState, useEffect } from 'react';
import { RootState } from '../../../reducer';
import { useSelector } from 'react-redux'
import { Typography, Switch, Select, Table, TableRow, TableCell } from './../../../tailwindcomponents';
import toolsApi from '../../../api/tools';
import { ToolData } from '../../../types/tool';
import { EmployeeSearch, EditAllowedDays } from '../../../components';
import { ReturnNameData } from '../../../components/Tools';
import { TableColumns } from '../../../types/table';

// getAllLoanedToolsbyDays takes a sort key and direction, but has no paging parameters and
// returns the whole filtered list, so the table sorts client-side and these only need to stay
// constant. These are the values the request has always carried.
const SERVER_SORT_KEY = "empty";
const SERVER_SORT_ASCENDING = true;

const sortTableColumns = [
    { label: "Name", key: "employee_name" },
    { label: "Tail number", key: "tail_number" },
    { label: "Description", key: "description" },
    { label: "Bay", key: "bay" },
    { label: "Area", key: "area" },
    { label: "Serial", key: "serial" },
    { label: "Loaned out", key: "out_date_text" },
    { label: "Allowed Days", key: "days_allowed" },
] as TableColumns[];

interface LoanedOutToolsProps {
  setLoading: (loading: boolean) => void;
}

function LoanedOutTools({ setLoading }: LoanedOutToolsProps) {
  const store = useSelector((state: RootState) => state);
  const isToolAdmin = store.user.me.user_clearence_groups.includes("Tool Stock");
  const [loaned_days, setLoanedDays] = useState("current day");
  const [tool_position, setToolPosition] = useState("All positions");
  const [employee_id, setEmployeeId] = useState(0);
  const [selected_row, setSelectedRow] = useState({} as ToolData);
  const [days_allowed, setDaysAllowed] = useState("");
  const [allowed_days_dialog_open, setAllowedDaysDialogOpen] = useState(false);
  const [loaned_out_tools, setLoanedOutTools] = useState(Array<ToolData>());
  const [tooLong, setTooLong] = useState<boolean>(false);

  useEffect(() => {
    const escFunction = (event: any) => {
      if (event.keyCode === 27) {
        setLoanedDays("current day");
        setToolPosition("All positions");
        setEmployeeId(0);
      }
    }
    document.addEventListener('keydown', escFunction, false);
    refreshData(loaned_days, tool_position, employee_id);
    const returnInput = document.getElementById('textfield');
    returnInput?.focus();
  }, [loaned_days, tool_position, employee_id, tooLong]);


  const generateTableBody = (row: ToolData, index: number) => {
    return (
      <TableRow className={row.tool_to_long_in_circulations === true ? "bg-red-300" : ""}>
        <TableCell wrap={false}>
          <ReturnNameData
            employee_id={row.employee_id}
            tail_number={row.tail_number}
            world_trip_name={row.world_trip_name}
            employee_name={row.employee_name}
          />
        </TableCell>
        <TableCell>{row.tail_number}</TableCell>
        <TableCell>{row.description}</TableCell>
        <TableCell>{row.bay}</TableCell>
        <TableCell>{row.area}</TableCell>
        <TableCell>{row.serial}</TableCell>
        <TableCell>{row.out_date_text}</TableCell>
        <TableCell onClick={() => onSelectedDaysAllowed(row)}>{row.days_allowed}</TableCell>
      </TableRow>
    )
  }

  const onSelectedDaysAllowed = (row: ToolData) => {
    if (isToolAdmin) {
      setDaysAllowed(row.days_allowed.toString());
      setSelectedRow(row);
      setAllowedDaysDialogOpen(true);
    }
  }

  const onSelectedLoanedDays = (value: string) => setLoanedDays(value);
  const onSelectToolPosition = (value: string) => setToolPosition(value);
  const onDaysAllowedChange = (days: number) => { setDaysAllowed(days.toString()) }

  function refreshData(loaned_days: string, tool_position: string, employee_id: number) {
    if (employee_id === null) { employee_id = 0; }
    const loaned_days_int = loaned_days === "current day" ? 0 : parseInt(loaned_days);
    setLoading(true);
    toolsApi.getAllLoanedToolsbyDays(loaned_days_int, tool_position, employee_id, SERVER_SORT_KEY, SERVER_SORT_ASCENDING, tooLong).then(res => {
      setLoanedOutTools(res);
    }).finally(() => setLoading(false));
  }
  const closeAllowedDaysDialog = (status: boolean) => {
    if (status === true) {
      selected_row.days_allowed = parseInt(days_allowed);
      setLoading(true);
      toolsApi.editDaysAllowed(selected_row).then(res => {
        refreshData(loaned_days, tool_position, employee_id);
      }).finally(() => setLoading(false));
    }
    setAllowedDaysDialogOpen(false);
  }
  return (
    <div className="w-full">
      <Typography text={`Loaned out tools overview (${loaned_out_tools.length})`} variant={'h2'} align={"center"} />
      <Typography text={"Press Esc to clear filters"} variant={'h5'} align={'center'} />
      <div className="flex flex-wrap justify-evenly">
        <div className="w-1/6">
          <Select
            onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) => onSelectedLoanedDays(e.target.value)}
            selectedItem={loaned_days.toString()}
            width={200}
            filter_by_text={"Loaned day's ago"}
            options={["current day", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "20", "30", "40", "50", "100"]}
          />
        </div>
        <div className="w-1/6">
          <Select
            onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) => onSelectToolPosition(e.target.value)}
            selectedItem={tool_position}
            width={200}
            filter_by_text={"Tools position"}
            options={["All positions", "Bay 1", "Bay 2", "Bay 3", "Bay 4", "Bay 5","Bay 6", "Bay 7", "Line","RKV/LINE", "Shops", "885 Hanger", "Other", "World Trips"]}
          />
        </div>
        <div className="flex flex-wrap w-1/6">
          <div className="w-1/3">
            <Typography text="Late Returns" variant="subtitle2" color="textPrimary" margin={[3, 0, 0, 0]} />
          </div>
          <div className="w-1/3 mt-6" >
            <Switch
              checked={!tooLong}
              onChange={() => setTooLong(!tooLong)}
              color="secondary"
              name="checkedB"
            />
          </div>
          <div className="w-1/4">
            <Typography text="Full List" variant="subtitle2" color="textPrimary" margin={[3, 0, 0, 0]} />
          </div>
        </div>
        <div className="w-1/3"> <EmployeeSearch place_holder={"Write employees name"} onSelectedEmployee={(employee_id: number) => setEmployeeId(employee_id)} /> </div>
      </div>
      <Table
        rows={loaned_out_tools}
        generateBody={(row: any, index: number) => generateTableBody(row, index)}
        pagination={false}
        sortTableColumns={sortTableColumns}
        skipIndexCol={true}
      />
      <EditAllowedDays
        visible={allowed_days_dialog_open}
        tool_description={selected_row.description}
        closeAllowedDaysDialog={(status: any) => closeAllowedDaysDialog(status)}
        days_allowed={days_allowed}
        onDaysAllowedChange={(days: number) => onDaysAllowedChange(days)}
      />
    </div>
  );
};
export default LoanedOutTools